/**
 * Live MLB Games Service - SportsData.io API
 *
 * Shared fetch/parsing logic for "games on a given date" data (scores, live
 * status, inning, baserunners). Used by:
 *  - src/components/GamesBanner.js (global ticker strip, date-selectable)
 *  - src/pages/league/LeagueScoreboard.js (MLB Scores right-rail, today only)
 *
 * Routed through the GraphQL Lambda proxy (getGamesByDate in
 * fantasyDataService.js) rather than calling api.sportsdata.io directly, so
 * the real SportsData.io key stays server-side (AWS Secrets Manager) instead
 * of shipping in the client bundle.
 *
 * Docs: https://sportsdata.io/developers/api-documentation/mlb#/scores/games-by-date
 */

import { getGamesByDate, FANTASY_DATA_ENABLED } from './fantasyDataService';

/**
 * Whether the games ticker / MLB rail should fetch live scores. Derives from
 * the single master switch in fantasyDataService (FANTASY_DATA_ENABLED) so
 * there's one place to flip the whole FantasyData integration on/off.
 *
 * While off, the loop-based consumers (GamesBanner, LeagueScoreboard) skip
 * polling entirely and render their built-in sample data instead.
 */
export const LIVE_GAMES_ENABLED = FANTASY_DATA_ENABLED;

// Statuses SportsData.io returns that we consider a "real" game worth showing.
// Excludes Postponed/Canceled/Suspended.
const REGULAR_SEASON_STATUSES = new Set([
  'SCHEDULED',
  'INPROGRESS',
  'FINAL',
  'F/OT',
  'DELAYED',
]);

export const isRegularSeasonGame = (game) => {
  const status = (game?.Status || '').toUpperCase();
  return (
    status !== 'POSTPONED' &&
    status !== 'CANCELED' &&
    status !== 'SUSPENDED' &&
    REGULAR_SEASON_STATUSES.has(status)
  );
};

export const filterRegularSeasonGames = (gamesArray) =>
  Array.isArray(gamesArray) ? gamesArray.filter(isRegularSeasonGame) : [];

/**
 * Fetch MLB games for a given date (YYYY-MM-DD) from SportsData.io.
 * Returns the filtered list of "regular" games (scheduled/live/final/delayed;
 * excludes postponed/canceled/suspended).
 *
 * Throws on network/HTTP/API errors - callers decide how to degrade (e.g.
 * GamesBanner.js falls back to sample data on error rather than showing blank).
 * Also throws immediately when LIVE_GAMES_ENABLED is false, so any direct
 * caller still hits its existing sample-data fallback path.
 */
export const fetchGamesForDate = async (dateString) => {
  if (!LIVE_GAMES_ENABLED) {
    throw new Error('Live MLB games are disabled (LIVE_GAMES_ENABLED is false).');
  }
  const gamesData = await getGamesByDate(dateString);
  return filterRegularSeasonGames(gamesData);
};

export default {
  fetchGamesForDate,
  filterRegularSeasonGames,
  isRegularSeasonGame,
};
